const toString = Object.prototype.toString

/**
 * 判断一个值的类型是否与给定的字符串表示的类型相匹配。
 *
 * 本函数通过调用Object.prototype.toString方法来获取一个值的类型，
 * 并与给定的类型字符串进行比较，以确定两者是否匹配。
 * 这种方法可以更准确地判断一个值的类型，而不是使用typeof操作符，
 * 因为typeof无法准确地区分一些内置类型，如null和array。
 *
 * @param val 要检查的值。
 * @param type 要与值的类型进行比较的字符串类型。
 * @returns 如果值的类型与给定的类型字符串匹配，则返回true；否则返回false。
 *
 * @example
 * is([], 'Array') // true
 * is({}, 'Object') // true
 * is('hello', 'String') // true
 * is(123, 'Number') // true
 * is(null, 'Null') // true
 * @source
 */
export function is(val: unknown, type: string): boolean {
  return toString.call(val) === `[object ${type}]`
}

/**
 * 判断一个值是否定义。
 *
 * 该函数用于类型断言，判断给定的参数是否在 TypeScript 的类型系统中被定义。
 * 通过这种方式，可以在运行时检查一个值的类型，进而决定如何处理这个值。
 *
 * @param val 可选参数，任意类型。用于检查是否定义的值。
 * @returns 如果 `val` 是定义过的，返回 true；否则返回 false。
 *
 * @example
 * isDef(123) // true
 * isDef(undefined) // false
 * isDef(null) // true
 * isDef('hello') // true
 * isDef({}) // true
 * isDef([]) // true
 * isDef(true) // true
 * isDef(NaN) // true
 * @source
 */
export function isDef<T = unknown>(val?: T): val is T {
  return typeof val !== 'undefined'
}

/**
 * 判断一个值是否未定义。
 *
 * 该函数用于类型断言，判断一个值是否未定义。如果值未定义，则断言成功，类型被视为与参数T相同。
 * 主要用于在类型系统中区分定义了和未定义的情况，尤其在可选参数或可能包含未定义值的场景中。
 *
 * @param val 可选参数，待检查的值。
 * @returns 如果val未定义，则返回true；否则返回false。
 *
 * @example
 * isUnDef(undefined) // true
 * isUnDef(null) // false
 * isUnDef(123) // false
 * isUnDef('hello') // false
 * isUnDef({}) // false
 * isUnDef([]) // false
 * isUnDef(true) // false
 * isUnDef(NaN) // false
 * @source
 */
export function isUnDef<T = unknown>(val?: T): val is T {
  return !isDef(val)
}

/**
 * 判断一个值是否为对象。
 *
 * 本函数用于确定给定的值是否为一个对象。特别地，它排除了null值，因为null虽然在JavaScript中被当作一个对象处理，
 * 但在很多情况下，我们不希望将null视为一个有效的对象。
 *
 * @param val 任意类型的值，用于检查是否为对象。
 * @returns 如果val是对象则返回true，否则返回false。
 * @example
 * isObject({}) // true
 * isObject([]) // false
 * isObject(null) // false
 * isObject(undefined) // false
 * isObject(1) // false
 * isObject('123') // false
 * @source
 */
export function isObject(val: any): val is Record<any, any> {
  return val !== null && is(val, 'Object')
}

/**
 * 判断一个值是否为空。
 *
 * 本函数用于判断不同类型的值是否为空。它支持数组、字符串、Map、Set以及对象的空值判断。
 * 对于数组和字符串，空值意味着其长度为0。对于Map和Set，空值意味着其大小为0。对于对象，空值意味着其拥有0个键。
 *
 * @param val 待判断的值，可以是任何类型。
 * @returns 如果`val`为空，则返回`true`；否则返回`false`。
 * @example
 * isEmpty([]) // true
 * isEmpty('') // true
 * isEmpty(new Map()) // true
 * isEmpty(new Set()) // true
 * isEmpty({}) // true
 * isEmpty([1, 2]) // false
 * @typeparam T - 泛型参数，用于指定`val`的类型，默认为`unknown`，允许任何类型。
 *
 * @source
 */
export function isEmpty<T = unknown>(val: T): val is T {
  // 判断val是否为数组或字符串，如果是，通过检查其长度来确定是否为空
  if (isArray(val) || isString(val)) {
    return val.length === 0
  }

  // 判断val是否为Map或Set，如果是，通过检查其大小来确定是否为空
  if (val instanceof Map || val instanceof Set) {
    return val.size === 0
  }
  // 判断val是否为对象，如果是，通过检查其键的数量来确定是否为空
  if (isObject(val)) {
    return Object.keys(val).length === 0
  }

  // 如果val不是上述任何一种类型，或者上述判断都不满足，则认为val不为空
  return false
}

/**
 * 判断给定值是否为Date对象。
 *
 * 本函数专为类型断言而设计，通过判断值的类型是否为Date，来动态地确定该值是否可以被视为Date对象。
 * 这对于在运行时处理不确定类型的值非常有用，特别是在需要将值强制转换为Date对象之前。
 *
 * @param val 待检查的值。
 * @returns 如果val是Date对象，则返回true；否则返回false。
 *
 * @source
 */
export function isDate(val: unknown): val is Date {
  return is(val, 'Date')
}

/**
 * 判断一个值是否为null。
 *
 * 该函数通过严格等于null的比较，来确定传入的值是否为null。这有助于在类型系统中动态地将值标记为null，
 * 从而在编译时捕获可能的错误，尤其是在与undefined的区分上。
 *
 * @param val 一个未知类型的值，需要被判断是否为null。
 * @returns 如果val是null，则返回true；否则返回false。由于返回类型是val is null，
 *          因此在类型断言中使用此函数可以将类型缩小到null。
 *
 * @source
 */
export function isNull(val: unknown): val is null {
  return val === null
}

/**
 * 判断一个值是否同时为null或undefined。
 *
 * 该函数通过调用isUnDef和isNull两个函数，来确定传入的值是否同时为undefined和null。
 * 这种检查对于需要明确区分null和undefined的场景非常有用，因为在JavaScript中，null和undefined在很多情况下被视为等同，
 * 但某些情况下需要对它们进行精确区分。
 *
 * @param val 一个未知类型的值，需要进行null和undefined的检查。
 * @returns 如果val同时为null和undefined，则返回true；否则返回false。
 *
 * @example
 * isNullAndUnDef(null) // true
 * isNullAndUnDef(undefined) // true
 *
 * @source
 */
export function isNullAndUnDef(val: unknown): val is null | undefined {
  return isUnDef(val) && isNull(val)
}

/**
 * 判断一个值是否为null或undefined。
 *
 * 这个函数提供了对null和undefined的联合类型检查。在TypeScript中，null和undefined被视为任何类型的子类型，
 * 因此，一个变量可能同时接受null和undefined作为值。这个函数的目的是在运行时准确地识别出这样的情况。
 *
 * @param val 待检查的值，可以是任何类型。
 * @returns 如果val是null或undefined，则返回true；否则返回false。
 * @example
 * isNullOrUnDef(null) // true
 * isNullOrUnDef(undefined) // true
 *
 * @source
 *
 */
export function isNullOrUnDef(val: unknown): val is null | undefined {
  // 通过调用isUnDef和isNull两个函数来检查val是否为undefined或null
  // 这两个函数的实现应分别详细说明如何检查undefined和null
  return isUnDef(val) || isNull(val)
}

/**
 * 判断给定值是否为数字。
 *
 * 本函数通过调用外部的is函数，并传入'Number'作为类型标识，来判断传入的值是否为数字类型。
 * 这种类型断言方式用于类型cript中，以动态地检查值的类型，并提供更精确的类型信息给编译器。
 *
 * @param val 未知类型的值，需要进行判断。
 * @returns 如果val是数字类型，则返回true；否则返回false。
 *
 * @example
 * isNumber(123) // true
 * isNumber('123') // false
 * isNumber(NaN) // true
 * isNumber(null) // false
 * isNumber(undefined) // false
 *
 * @source
 */
export function isNumber(val: unknown): val is number {
  return is(val, 'Number')
}

/**
 * 判断一个值是否为Promise实例。
 *
 * 本函数用于类型断言，即在运行时检查一个值是否为Promise类型。这在处理异步操作或需要对Promise进行类型检查的场景中非常有用。
 * 通过判断val是否具有Promise的特征（例如then和catch方法），来确定val是否为Promise实例。
 *
 * @param val 待检查的值，类型为unknown，表示可以是任何类型。
 * @returns 如果val是Promise实例，则返回true；否则返回false。
 * @example
 * isPromise(new Promise((resolve, reject) => {})) // true
 * isPromise([]) // false
 * @source
 */
export function isPromise<T = any>(val: unknown): val is Promise<T> {
  // 使用多个条件判断来确认val是否满足Promise的定义
  return (
    is(val, 'Promise') &&
    val instanceof Promise &&
    typeof val === 'object' &&
    isFunction(val.then) &&
    isFunction(val.catch)
  )
}

/**
 * 判断给定值是否为字符串类型。
 *
 * 本函数通过调用外部的is函数，并传入特定的类型标识符'String'，来判断传入的值是否为字符串类型。
 * 这种类型保护机制允许在运行时更准确地确定值的类型，从而在类型系统中提供更精确的类型信息。
 *
 * @param val 待检查的值，其类型为unknown，表示它可以是任何类型。
 * @returns 如果val是字符串类型，则返回true；否则返回false。
 * @example
 *
 * isString('hello') // true
 * isString(123) // false
 *
 * @source
 */
export function isString(val: unknown): val is string {
  return is(val, 'String')
}

/**
 * 判断给定值是否为函数。
 *
 * 函数是用于执行特定任务的可重复使用的代码块。本函数的目的是确定传入的值是否具有函数类型。
 * 它通过使用typeof操作符检查值的类型，并与字符串'function'进行比较来实现这一点。
 * 如果类型为函数，则返回true；否则返回false。
 *
 * @param val 待检查的值。
 * @returns 如果val是函数，则返回true；否则返回false。
 * @example
 * isFunction(() => {}) // true
 * isFunction('function') // false
 * isFunction(true) // false
 * isFunction(null) // false
 * @source
 */
export function isFunction(val: unknown): boolean {
  return typeof val === 'function'
}

/**
 * 判断给定值是否为布尔类型。
 *
 * 本函数通过调用外部的 `is` 函数，检查传入值的类型是否为 `Boolean`。
 * 这种类型检查对于确保代码中布尔类型的正确性和一致性很有必要。
 * 使用类型保护语法 `val is boolean`，使得该函数不仅返回一个布尔值，
 * 还告诉TypeScript编译器如果函数返回`true`，则`val`可以被视为`boolean`类型。
 *
 * @param val 未知类型的值，需要进行判断。
 * @returns 如果`val`是布尔类型，则返回`true`；否则返回`false`。
 * @example
 * isBoolean(true) // true
 * isBoolean(false) // true
 * isBoolean(0) // false
 * isBoolean('true') // false
 * @source
 */
export function isBoolean(val: unknown): val is boolean {
  return is(val, 'Boolean')
}

/**
 * 判断给定值是否为正则表达式对象。
 *
 * @param val 未知类型的值，需要进行判断。
 * @returns 如果`val`是正则表达式对象，则返回`true`；否则返回`false`。
 *
 * @source
 */
export function isRegExp(val: unknown): val is RegExp {
  return is(val, 'RegExp')
}

/**
 * 判断一个值是否为数组。
 * 此函数通过`Array.isArray`方法来确定一个值是否为数组。
 * 它的存在是为了提供一种类型守卫，使得在TypeScript中可以更安全地
 * 判断一个变量是否为数组类型，从而在类型系统中得到更好的支持。
 *
 * @param val 任意类型的值，用于检查是否为数组。
 * @returns 如果`val`是数组，则返回`true`；否则返回`false`。
 * @example
 * isArray([1, 2, 3]) // true
 * isArray([]) // true
 * isArray({}) // false
 * isArray('hello') // false
 * @source
 */
export function isArray(val: any): val is Array<any> {
  if (!val) return false
  return val && Array.isArray(val)
}

/**
 * 判断给定的值是否为Window对象。
 *
 * 该函数主要用于在跨框架或WebWorker的场景下，判断当前环境是否处于一个Window上下文中。
 * 由于在不同的执行环境中，Window对象的定义和存在与否可能会有所不同，因此需要通过动态判断的方式来确定。
 *
 * @param val 任意类型的值，用于检查是否为Window对象。
 * @returns 如果val是Window对象，则返回true；否则返回false。
 * @example

 * isWindow(window) // true
 * isWindow({}) // false

 * @source
 */
export function isWindow(val: any): val is Window {
  return typeof window !== 'undefined' && is(val, 'Window')
}

/**
 * 判断给定的值是否为HTML或SVG元素。
 *
 * 该函数通过检查值是否为对象且具有tagName属性来确定是否为元素。
 * 这种检查方式适用于HTML和SVG元素，因为它们都继承自Element接口，
 * 并且都具有tagName属性，tagName 包含了 element 元素的标签名。
 *
 * @param val 待检查的值。
 * @returns 如果val是元素，则返回true；否则返回false。
 * @example
 * isElement(document.createElement('div')) // true
 * isElement({}) // false
 * isElement(null) // false
 * @source
 */
export function isElement(val: unknown): val is Element {
  // 先判断val是否为对象，然后再检查是否有tagName属性，以确定是否为元素。
  return (
    !!val &&
    typeof val === 'object' &&
    'tagName' in val &&
    (val as Element).nodeType === 1
  )
}

/**
 * 判断给定的值是否为Map对象。
 *
 * Map对象是一种键值对的集合，其中键可以是任何类型，而不仅仅是字符串或符号。
 * 这个函数的目的是提供一种类型保护机制，用于在运行时确定一个值是否为Map对象。
 *
 * @param val 未知类型的值，需要进行判断。
 * @returns 如果val是Map对象，则返回true；否则返回false。
 * @example
 * isMap(new Map()) // true
 * isMap({}) // false
 * isMap(null) // false
 * @source
 */
export function isMap(val: unknown): val is Map<any, any> {
  // 使用is函数检查val是否为Map对象。
  // is函数的实现假设它能够正确识别Map对象。
  return is(val, 'Map')
}

/**
 * 判断当前是否运行在服务器端
 *
 * @returns 如果当前环境运行在服务器端则返回true，否则返回false
 * @example
 * isServer() // 当前环境运行在服务器端 返回 true 否则 false
 */
export function isServer() {
  return typeof window === 'undefined'
}

/**
 * 判断是否在客户端环境中运行
 *
 * @returns 如果在客户端环境中运行则返回true，否则返回false
 * @example
 * isClient() // true 或者 false
 */
export function isClient() {
  return typeof window !== 'undefined' && !!window.document && !!window.location
}
/**
 * 检查给定的路径是否为URL。
 *
 * 该函数通过正则表达式匹配来判断一个字符串是否符合URL的格式。
 * 它支持http和https协议的URL，以及不带协议的www形式的URL。
 * 此外，它还能处理URL中的各种常见元素，如路径、查询参数和锚点。
 *
 * @param path 要检查的字符串路径。
 * @returns 如果路径是URL，则返回true；否则返回false。
 * @example
 * isUrl('https://www.baidu.com') // true
 * isUrl('http://example.com') // true
 * isUrl('') // false
 * @source
 */
export function isUrl(path: string): boolean {
  // 定义一个正则表达式，用于匹配URL的模式。
  const reg = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  )
  // 使用正则表达式测试函数来检查路径是否匹配URL的模式。
  return reg.test(path)
}
